import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import {
    DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChevronDown, UserRound, KeyRound, Building2, Mail, LogOut } from "lucide-react";

export const PortalProfileMenu = () => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [profile, setProfile] = useState(null);
    const [cur, setCur] = useState("");
    const [nw, setNw] = useState("");
    const [cf, setCf] = useState("");
    const [saving, setSaving] = useState(false);

    const openProfile = async () => {
        setOpen(true);
        setCur(""); setNw(""); setCf("");
        try {
            const { data } = await api.get("/portal/profile");
            setProfile(data);
        } catch {
            setProfile(null);
        }
    };

    const submit = async () => {
        if (!cur || !nw) return toast.error("Complete los campos");
        if (nw.length < 6) return toast.error("La nueva contraseña debe tener al menos 6 caracteres");
        if (nw !== cf) return toast.error("Las contraseñas no coinciden");
        setSaving(true);
        try {
            await api.post("/portal/change-password", { current_password: cur, new_password: nw });
            toast.success("Contraseña actualizada");
            setCur(""); setNw(""); setCf("");
            setOpen(false);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "No se pudo cambiar la contraseña");
        } finally { setSaving(false); }
    };

    return (
        <>
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <button className="flex items-center gap-1.5 text-sm rounded-lg px-2 py-1 hover:bg-secondary transition-colors" data-testid="portal-user-menu-trigger">
                        <span className="hidden sm:inline text-muted-foreground">Hola, </span>
                        <span className="font-medium">{user?.full_name?.split(" ")[0]}</span>
                        <ChevronDown className="w-4 h-4 text-muted-foreground" />
                    </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                    <DropdownMenuItem onClick={openProfile} data-testid="portal-open-profile">
                        <UserRound className="w-4 h-4 mr-2" /> Mi perfil
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => { logout(); navigate("/login"); }} className="text-red-600 focus:text-red-600" data-testid="portal-logout">
                        <LogOut className="w-4 h-4 mr-2" /> Cerrar sesión
                    </DropdownMenuItem>
                </DropdownMenuContent>
            </DropdownMenu>

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-md" data-testid="portal-profile-dialog">
                    <DialogHeader>
                        <DialogTitle>Mi perfil</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-1">
                        <div className="rounded-lg border p-3 space-y-2 bg-muted/30">
                            <div className="flex items-center gap-2 text-sm">
                                <UserRound className="w-4 h-4 text-muted-foreground" />
                                <span className="font-medium">{profile?.full_name || user?.full_name}</span>
                            </div>
                            {(profile?.email || profile?.username) && (
                                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                                    <Mail className="w-4 h-4" />
                                    {profile?.email || profile?.username}
                                </div>
                            )}
                            {profile?.branch_scope === "specific" && (profile?.branches?.length > 0) && (
                                <div className="flex items-start gap-2 text-sm text-muted-foreground">
                                    <Building2 className="w-4 h-4 mt-0.5 shrink-0" />
                                    <span>Sucursales asignadas: {profile.branches.join(", ")}</span>
                                </div>
                            )}
                        </div>

                        <div className="space-y-3">
                            <div className="flex items-center gap-2 text-sm font-semibold">
                                <KeyRound className="w-4 h-4" /> Cambiar contraseña
                            </div>
                            <div>
                                <Label className="text-xs">Contraseña actual</Label>
                                <Input type="password" value={cur} onChange={(e) => setCur(e.target.value)} data-testid="profile-current-pw" />
                            </div>
                            <div>
                                <Label className="text-xs">Nueva contraseña</Label>
                                <Input type="password" value={nw} onChange={(e) => setNw(e.target.value)} data-testid="profile-new-pw" />
                            </div>
                            <div>
                                <Label className="text-xs">Confirmar nueva contraseña</Label>
                                <Input type="password" value={cf} onChange={(e) => setCf(e.target.value)} data-testid="profile-confirm-pw" />
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                        <Button onClick={submit} disabled={saving} data-testid="profile-change-pw-save">
                            {saving ? "Guardando…" : "Cambiar contraseña"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
};
